import "./KickStarter.css";
import CardColorBlock from "../../../components/cards/CardColorBlock/CardColorBlock";
import HighlightText from "../../../components/texts/HighlightText/HighlightText";
import Title from "../../../components/common/Title/Title";
import PricingCard from "../../../components/cards/PricingCard/PricingCard";
import SEO from "../../../components/common/SEO/SEO";
import OptimizedImage from "../../../components/common/OptimizedImage/OptimizedImage";
import { useTranslation, Trans } from "react-i18next";

export default function KickStarter() {
  const { t } = useTranslation("kickstarter");

  const header = t("header", { returnObjects: true });
  const introduction = t("introduction", { returnObjects: true }) || {};
  const steps = t("steps", { returnObjects: true }) || [];
  const whatYouGet = t("whatYouGet", { returnObjects: true }) || {};
  const howItWorks = t("howItWorks", { returnObjects: true }) || {};
  const stepsSection = t("stepsSection", { returnObjects: true }) || {};
  const packages = t("packages", { returnObjects: true }) || {};
  const notes = t("notes", { returnObjects: true }) || [];
  const quote = t("quote", { returnObjects: true }) || {};

  const stepMeta = [
    { iconClassName: "fas fa-sitemap", iconStyle: { fontSize: "70px", color: "white" }, color: "var(--color-blue)" },
    { iconClassName: "fas fa-search-dollar", iconStyle: { fontSize: "70px", color: "white" }, color: "var(--color-blue)" },
    { iconClassName: "fas fa-lightbulb", iconStyle: { fontSize: "70px", color: "white" }, color: "var(--color-blue)" },
    { iconClassName: "far fa-compass", iconStyle: { fontSize: "70px", color: "white" }, color: "var(--color-blue)" },
    { image: "/images/icons/robot_arm_white.webp", color: "var(--color-orange)" }
  ];

  return (
    <>
      <SEO
        title={t("seo.title")}
        description={t("seo.description")}
        url={t("seo.url")}
        image={t("seo.image")}
      />
      <div className="main-container">
        <main className="head-content">
          <div className="column-content-container height-container">
            <Title text={header.title} size={["4rem", "3rem", "2rem"]} level="h1" />
            <HighlightText text={header.tagline} size={["1.4rem", "1.2rem", "1rem"]} padding={["50px", "30px 24px", "20px"]} fontWeight="500" alignItems="flex-start" />
            <p className="paragraph-content">
              {header.paragraph}
            </p>
          </div>

          <div className="cards-container">
            {steps.map((s, i) => {
              const meta = stepMeta[i] || {};
              return (
                <CardColorBlock
                  key={i}
                  iconClassName={meta.iconClassName}
                  iconStyle={meta.iconStyle}
                  image={meta.image}
                  color={meta.color || "var(--color-blue)"}
                  title={s.title}
                  text={<Trans i18nKey={`steps.${i}.text`} ns="kickstarter" />}
                />
              );
            })}
          </div>
        </main>

        <div className="page-content-container">
          <section className="column-content-container" style={{ alignItems: "center" }}>
            <div style={{ alignSelf: "flex-start" }}>
              <Title text={introduction.title} size={["2rem", "1.8rem", "1.5rem"]} />
            </div>

            <p className="paragraph-content">
              {introduction.paragraph}
            </p>

            <div className="kickstarter-quote">
              <span style={{ fontStyle: "italic" }}>
                {quote.text}
              </span>
              <a
                href="https://www.linkedin.com/in/robbertbrand1988/"
                target="_blank"
                rel="noopener noreferrer"
                className="link"
                style={{ marginLeft: 12 }}
              >
                {quote.author}
              </a>
            </div>

            <p className="paragraph-content">
              {introduction.closing}
            </p>
          </section>

          <section className="row-content-container" style={{ alignItems: "center" }}>
            <OptimizedImage
              src="/images/industries/factory2.webp"
              className="factoryChartPie"
              alt="Factory"
            />
            <div className="column-content-container kickstart-highlight-container">
              <Title text={whatYouGet.title} size={["2rem", "1.8rem", "1.5rem"]} />
              <p className="paragraph-content">{whatYouGet.paragraph}</p>
              <div className="page-highlight-container">
                {(whatYouGet.highlights || []).map((h, idx) => (
                  <HighlightText key={idx} text={h} fontWeight="500" size={["1rem", "0.8rem"]} />
                ))}
              </div>
            </div>
          </section>

          <section className="column-content-container">
            <Title text={howItWorks.title} size={["2rem", "1.8rem", "1.5rem"]} />
            <p className="paragraph-content">
            <Trans
              i18nKey="howItWorks.description"
              ns="kickstarter"
              components={[<a className="text-link" href="/expertise/cyber-security" />]}
            />
            </p>

            <div className="page-highlight-container">
              {(howItWorks.bullets || []).map((b, idx) => (
                <HighlightText key={idx} text={b} size={["1rem", "0.8rem"]}/>
              ))}
            </div>

            <p className="paragraph-content">{howItWorks.closing}</p>
          </section>

          <section className="column-content-container">
            <Title text={stepsSection.title} size={["2rem", "1.8rem", "1.5rem"]} />
            <p className="paragraph-content">{stepsSection.paragraph}</p>

            <div className="page-highlight-container">
              {(stepsSection.bullets || []).map((b, idx) => (
                <HighlightText key={idx} size={["1rem", "0.8rem"]} text={<Trans i18nKey={`stepsSection.bullets.${idx}`} ns="kickstarter" />} />
              ))}
            </div>
          </section>

          <section style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
            <div style={{ alignSelf: "flex-start" }}>
              <Title text={packages.title} size={["2rem", "1.8rem", "1.5rem"]} />
            </div>

            <div className="row-content-container" style={{ gap: "20px" }}>
              {["basic", "advanced", "supreme"].map((k) => {
                const pkg = packages?.[k];
                if (!pkg) return null;
                return (
                  <PricingCard
                    key={k}
                    title={pkg.title}
                    subtitle={pkg.subtitle}
                    items={pkg.items}
                  />
                );
              })}
            </div>

            <div className="page-highlight-container">
              {notes.map((n, idx) => (
                <HighlightText key={idx} text={n} size={["1rem", "0.8rem"]} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};